/**
 * Export utilities for interactive components
 */
import React from 'react';

/**
 * Export data as CSV file
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name for the downloaded file
 * @param {Array} columns - Optional column configuration [{key, title}]
 */
export const exportToCSV = (data, filename = 'export', columns = null) => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Get headers from columns config or from data keys
  const headers = columns
    ? columns.map(col => col.title || col.key)
    : Object.keys(data[0]);

  const keys = columns
    ? columns.map(col => col.dataKey || col.key)
    : Object.keys(data[0]);

  // Build CSV content
  const csvContent = [
    headers.join(','),
    ...data.map(row =>
      keys.map(key => {
        const value = row[key];
        // Handle values that need quoting (strings with commas, quotes, or newlines)
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value ?? '';
      }).join(',')
    )
  ].join('\n');

  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `${filename}.csv`);
};

/**
 * Export data as Excel file (XLSX format using simple XML)
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name for the downloaded file
 * @param {Array} columns - Optional column configuration [{key, title}]
 */
export const exportToExcel = (data, filename = 'export', columns = null) => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Get headers from columns config or from data keys
  const headers = columns
    ? columns.map(col => col.title || col.key)
    : Object.keys(data[0]);

  const keys = columns
    ? columns.map(col => col.dataKey || col.key)
    : Object.keys(data[0]);

  // Build HTML table for Excel (simple approach that works without dependencies)
  const tableRows = [
    `<tr>${headers.map(h => `<th style="font-weight:bold;background:#f3f4f6">${escapeHtml(h)}</th>`).join('')}</tr>`,
    ...data.map(row =>
      `<tr>${keys.map(key => `<td>${escapeHtml(String(row[key] ?? ''))}</td>`).join('')}</tr>`
    )
  ].join('');

  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
    <head><meta charset="UTF-8"></head>
    <body><table border="1">${tableRows}</table></body>
    </html>
  `;

  const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
  downloadBlob(blob, `${filename}.xls`);
};

/**
 * Export chart/element as PNG image
 * @param {HTMLElement} element - DOM element to capture
 * @param {string} filename - Name for the downloaded file
 */
export const exportToPNG = async (element, filename = 'chart') => {
  if (!element) {
    console.warn('No element to export');
    return;
  }

  try {
    // Dynamic import of html2canvas (if available) or use canvas approach for SVG
    const svgElement = element.querySelector('svg');

    if (svgElement) {
      // SVG-based export (works for Recharts)
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      // Get dimensions
      const bbox = svgElement.getBoundingClientRect();
      const scale = 2; // Higher resolution
      canvas.width = bbox.width * scale;
      canvas.height = bbox.height * scale;
      ctx.scale(scale, scale);

      // Fill white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      return new Promise((resolve, reject) => {
        img.onload = () => {
          ctx.drawImage(img, 0, 0);
          canvas.toBlob((blob) => {
            if (blob) {
              downloadBlob(blob, `${filename}.png`);
              resolve();
            } else {
              reject(new Error('Failed to create blob'));
            }
          }, 'image/png');
        };
        img.onerror = reject;
        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
      });
    } else {
      // Fallback: try html2canvas if available
      if (typeof window !== 'undefined' && window.html2canvas) {
        const canvas = await window.html2canvas(element, {
          backgroundColor: '#ffffff',
          scale: 2,
        });
        canvas.toBlob((blob) => {
          if (blob) {
            downloadBlob(blob, `${filename}.png`);
          }
        }, 'image/png');
      } else {
        console.warn('Cannot export non-SVG element without html2canvas library');
      }
    }
  } catch (error) {
    console.error('Error exporting to PNG:', error);
  }
};

/**
 * Copy data to clipboard as tab-separated values (for pasting into Excel/Sheets)
 * @param {Array} data - Array of objects to copy
 * @param {Array} columns - Optional column configuration
 */
export const copyToClipboard = async (data, columns = null) => {
  if (!data || data.length === 0) {
    console.warn('No data to copy');
    return false;
  }

  const headers = columns
    ? columns.map(col => col.title || col.key)
    : Object.keys(data[0]);

  const keys = columns
    ? columns.map(col => col.dataKey || col.key)
    : Object.keys(data[0]);

  const tsvContent = [
    headers.join('\t'),
    ...data.map(row => keys.map(key => row[key] ?? '').join('\t'))
  ].join('\n');

  try {
    await navigator.clipboard.writeText(tsvContent);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
};

// Helper functions
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const escapeHtml = (str) => {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
};

// Export dropdown component for reuse
export const ExportButton = ({
  onExportCSV,
  onExportExcel,
  onExportPNG,
  onCopy,
  showPNG = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 hover:border-gray-300 transition-colors"
        title="Export data"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        Export
        <svg className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 z-20 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
          {onExportCSV && (
            <button
              onClick={() => { onExportCSV(); setIsOpen(false); }}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              CSV
            </button>
          )}
          {onExportExcel && (
            <button
              onClick={() => { onExportExcel(); setIsOpen(false); }}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Excel
            </button>
          )}
          {showPNG && onExportPNG && (
            <button
              onClick={() => { onExportPNG(); setIsOpen(false); }}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              PNG Image
            </button>
          )}
          {onCopy && (
            <button
              onClick={() => { onCopy(); setIsOpen(false); }}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
              </svg>
              Copy
            </button>
          )}
        </div>
      )}
    </div>
  );
};

